import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, QrCode, Award, CalendarDays, Users, BookOpen,
  ClipboardCheck, BarChart3, ArrowLeft, ShieldCheck, Sparkles
} from "lucide-react";

const HERO = "/campus-hero.jpg";
const PATTERN = "https://images.unsplash.com/photo-1542414110-ae27fdb87ee1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxpc2xhbWljJTIwZ2VvbWV0cmljJTIwYXJjaGl0ZWN0dXJlJTIwbGlnaHR8ZW58MHx8fHwxNzg2MjgxODA4fDA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: BookOpen, title: "إدارة البرامج", desc: "إنشاء البرامج والدورات العلمية وإدارتها من مكان واحد." },
  { icon: QrCode, title: "حضور بالـ QR", desc: "تسجيل الحضور بمسح رمز QR مرتبط بوقت المحاضرة." },
  { icon: Award, title: "الشهادات والساعات", desc: "احتساب الساعات العلمية تلقائيًا وإصدار الشهادات." },
  { icon: ClipboardCheck, title: "الواجبات والدرجات", desc: "رفع الواجبات وتقييمها واحتساب النتيجة النهائية." },
  { icon: CalendarDays, title: "التقويم العلمي", desc: "جدول المحاضرات مع منع التعارض بين الوحدات السكنية." },
  { icon: BarChart3, title: "تقارير وإحصائيات", desc: "متابعة الحضور والإنجاز عبر لوحات تحكم ذكية." },
];

const STAT_LABELS = [
  { key: "programs", l: "برنامج علمي" },
  { key: "students", l: "طالب مسجّل" },
  { key: "teachers", l: "محاضر" },
  { key: "lectures", l: "محاضرة" },
];

export default function Landing() {
  const [counts, setCounts] = useState({ programs: 0, students: 0, teachers: 0, lectures: 0 });
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/stats`)
      .then((r) => setCounts(r.data))
      .catch(() => {});
  }, []);
  const stats = STAT_LABELS.map((s) => ({ v: counts[s.key] ?? 0, l: s.l }));
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-gold">
              <GraduationCap className="h-6 w-6" />
            </div>
            <div className="leading-tight">
              <p className="font-heading text-sm font-extrabold text-navy">سُفراء</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button data-testid="header-login-btn" variant="ghost" className="rounded-xl text-navy">تسجيل الدخول</Button></Link>
            <Link to="/register"><Button data-testid="header-register-btn" className="rounded-xl bg-navy hover:bg-navy-light">إنشاء حساب</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:py-24">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 text-sm font-semibold text-gold-dark">
              <Sparkles className="h-4 w-4" /> منصة علمية جامعية حديثة
            </span>
            <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.4] text-navy md:text-5xl lg:text-6xl">
              <span className="text-greenx">سُفراء</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-[1.9] text-slate-500 md:text-lg">
              منصة متكاملة لإدارة الدورات والأنشطة العلمية: من التسجيل والحضور بالـ QR،
              إلى الواجبات والدرجات والشهادات والتقارير، بواجهة عربية أنيقة وسهلة.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register"><Button data-testid="hero-cta-btn" size="lg" className="rounded-xl bg-navy px-8 hover:bg-navy-light active:scale-95">
                ابدأ الآن <ArrowLeft className="me-2 h-5 w-5" />
              </Button></Link>
              <Link to="/login"><Button data-testid="hero-login-btn" size="lg" variant="outline" className="rounded-xl border-greenx px-8 text-greenx hover:bg-emerald-50">
                لديك حساب؟ دخول
              </Button></Link>
            </div>
          </div>
          <div className="relative animate-scale-in">
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-greenx/10 to-gold/10 blur-2xl" />
            <img src={HERO} alt="حرم جامعي" className="relative h-[420px] w-full rounded-[2rem] object-cover soft-shadow" />
            <div className="absolute -bottom-6 start-6 rounded-2xl border border-slate-100 bg-white p-4 soft-shadow">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-greenx"><ShieldCheck className="h-6 w-6" /></div>
                <div>
                  <p className="font-heading text-sm font-bold text-navy">حضور موثّق</p>
                  <p className="text-xs text-slate-400">مرتبط بوقت المحاضرة</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative overflow-hidden bg-navy py-14">
        <img src={PATTERN} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.06]" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-2 gap-8 px-5 md:grid-cols-4">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <p className="font-heading text-4xl font-extrabold text-gold">{s.v}</p>
              <p className="mt-1 text-sm text-slate-300">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-5 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-2xl font-bold text-navy md:text-4xl">كل ما تحتاجه لإدارة البرامج</h2>
          <p className="mt-3 text-slate-500">نظام موحد ومترابط يغطي دورة حياة البرنامج العلمي بالكامل.</p>
        </div>
        <div className="stagger mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <div key={i} className="card-hover rounded-2xl border border-slate-100 bg-white p-7 soft-shadow">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy">
                <f.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-heading text-lg font-bold text-navy">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 pb-20">
        <div className="relative overflow-hidden rounded-[2rem] bg-greenx px-8 py-14 text-center">
          <img src={PATTERN} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-10" />
          <div className="relative">
            <h2 className="font-heading text-2xl font-bold text-white md:text-3xl">انضم إلى المنصة العلمية اليوم</h2>
            <p className="mx-auto mt-3 max-w-lg text-emerald-50">سجّل حسابك وابدأ بالتسجيل في البرامج العلمية المتاحة في مدينة حلب.</p>
            <Link to="/register"><Button data-testid="cta-register-btn" size="lg" className="mt-7 rounded-xl bg-gold px-10 text-navy hover:bg-gold-light active:scale-95">إنشاء حساب مجاني</Button></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-100 py-8 text-center text-sm text-slate-400">
        © {new Date().getFullYear()} سُفراء
      </footer>
    </div>
  );
}
